// Drawer — right- or left-side panel with scrim, focus trap, ESC to close.

import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { IconButton } from "./IconButton";
import { Icon } from "@/lib/icons";

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  side?: "left" | "right";
  title?: ReactNode;
  width?: number | string;
  ariaLabel?: string;
}

export function Drawer({ open, onClose, children, side = "right", title, width = 480, ariaLabel }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
      }
    };
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    const restore = restoreRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      restore?.focus?.();
    };
  }, [open, onClose]);

  return (
    <div className={cn("lex-drawer", open && "lex-drawer--open")} aria-hidden={!open}>
      <div className="lex-drawer__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="lex-drawer__panel"
        data-side={side}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : ariaLabel ?? "Drawer"}
        tabIndex={-1}
        style={{ width: typeof width === "number" ? `${width}px` : width }}
      >
        {title != null ? (
          <header className="lex-drawer__header">
            <h2 className="lex-drawer__title">{title}</h2>
            <IconButton label="Close drawer" onClick={onClose}><Icon.X size={18} /></IconButton>
          </header>
        ) : null}
        <div className="lex-drawer__body">{children}</div>
      </div>
    </div>
  );
}

export default Drawer;
